/**
 * Shared chrome for party-game host screens: end / waiting / result frames + playing header.
 * Game-specific question UI stays in each HostDisplay.
 */
import React, { type ReactNode } from 'react';
import Header from '../shared/Header';
import { HeaderIcons } from '../shared/icons';
import { DesktopLayout, StandingsSidebar, type LiveAnswer } from '../shared';
import { GameEndScreen } from './GameEndScreen';
import type { GameStatistics } from '../../types/game';
import type { PartyLeaderboardEntry } from '../../hooks/usePartyHostGameLifecycle';
import { useTranslation } from '../../i18n';
import '../../styles/party-host.css';

export type PartyHostTheme = {
  primary: string;
  primaryLight: string;
  primaryBorder: string;
  accent: string;
  accentLight: string;
  accentBorder: string;
};

export type PartyHostPlayer = {
  id: string;
  name: string;
  score: number;
  avatar?: string;
};

type GameEndData = {
  finalLeaderboard: PartyLeaderboardEntry[];
  gameStatistics: GameStatistics | null;
} | null;

type PartyHostGameEndProps = {
  roomName: string;
  gameType: string;
  finalLeaderboard: PartyLeaderboardEntry[];
  gameStatistics: GameStatistics | null;
  gameEndData: GameEndData;
  onPlaySameGame: () => void;
  onBackToGameSelection?: () => void;
  getPlayerAvatar?: (playerId: string) => string | undefined;
};

export function PartyHostGameEnd({
  roomName,
  gameType,
  finalLeaderboard,
  gameStatistics,
  gameEndData,
  onPlaySameGame,
  onBackToGameSelection,
  getPlayerAvatar,
}: PartyHostGameEndProps) {
  const displayLeaderboard =
    finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? []);
  const displayStatistics = gameStatistics ?? gameEndData?.gameStatistics ?? null;

  return (
    <GameEndScreen
      finalLeaderboard={displayLeaderboard}
      gameStatistics={displayStatistics}
      onPlaySameGame={onPlaySameGame}
      onPlayAnotherGame={onBackToGameSelection || (() => {})}
      onBackToGameSelection={onBackToGameSelection}
      roomName={roomName}
      gameType={gameType}
      getPlayerAvatar={getPlayerAvatar}
    />
  );
}

type PartyHostWaitingProps = {
  roomName: string;
  emoji: string;
  onBackToGameSelection?: () => void;
  children?: ReactNode;
};

export function PartyHostWaiting({
  roomName,
  emoji,
  onBackToGameSelection,
  children,
}: PartyHostWaitingProps) {
  const { t } = useTranslation();

  return (
    <div className="party-host-shell">
      <Header
        leftButton={
          onBackToGameSelection
            ? {
                icon: HeaderIcons.back,
                onClick: onBackToGameSelection,
                tooltip: t('gameplay.backToGameSelection'),
              }
            : undefined
        }
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage'),
        }}
        roomCode={roomName}
      />
      <div className="party-host-shell__body party-host-shell__body--centered">
        <div className="party-host-waiting__emoji" aria-hidden>
          {emoji}
        </div>
        <h2 className="party-host-waiting__title">{t('gameplay.hostWaitingNextQuestion')}</h2>
        {children}
      </div>
    </div>
  );
}

type PartyHostResultFrameProps = {
  roomName: string;
  onBackToGameSelection?: () => void;
  children: ReactNode;
};

/** Fullscreen gradient + header; put result content as children. */
export function PartyHostResultFrame({
  roomName,
  onBackToGameSelection,
  children,
}: PartyHostResultFrameProps) {
  const { t } = useTranslation();

  return (
    <div className="party-host-shell">
      <Header
        leftButton={
          onBackToGameSelection
            ? {
                icon: HeaderIcons.back,
                onClick: onBackToGameSelection,
                tooltip: t('gameplay.backToGameSelection'),
              }
            : undefined
        }
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage'),
        }}
        roomCode={roomName}
      />
      <div className="party-host-shell__body">{children}</div>
    </div>
  );
}

type PartyHostCorrectAnswerCardProps = {
  correctAnswer: ReactNode;
};

export function PartyHostCorrectAnswerCard({ correctAnswer }: PartyHostCorrectAnswerCardProps) {
  const { t } = useTranslation();

  return (
    <div className="party-host-correct-answer">
      <div className="party-host-correct-answer__label">{t('gameplay.correctAnswerLabel')}</div>
      <div className="party-host-correct-answer__value">{correctAnswer}</div>
    </div>
  );
}

type PartyHostCorrectAnswerResultProps = {
  roomName: string;
  correctAnswer: ReactNode;
  onBackToGameSelection?: () => void;
  /** When set, shows standings beside the correct-answer card (Stoned Yet–style). */
  standings?: {
    players: PartyHostPlayer[];
    liveAnswers: LiveAnswer[];
    questionId?: string;
    theme: PartyHostTheme;
  };
};

export function PartyHostCorrectAnswerResult({
  roomName,
  correctAnswer,
  onBackToGameSelection,
  standings,
}: PartyHostCorrectAnswerResultProps) {
  const card = (
    <div className="party-host-correct-answer__wrap">
      <PartyHostCorrectAnswerCard correctAnswer={correctAnswer} />
    </div>
  );

  return (
    <PartyHostResultFrame roomName={roomName} onBackToGameSelection={onBackToGameSelection}>
      {standings ? (
        <DesktopLayout
          leftContent={card}
          rightContent={
            <StandingsSidebar
              players={standings.players}
              liveAnswers={standings.liveAnswers}
              totalPlayers={standings.players.length}
              questionId={standings.questionId}
              shouldUpdateScores={true}
              theme={standings.theme}
            />
          }
          rightWidth="320px"
        />
      ) : (
        card
      )}
    </PartyHostResultFrame>
  );
}

type PartyHostPlayingHeaderProps = {
  gameLabel: string;
  roomName: string;
  theme: PartyHostTheme;
};

export function PartyHostPlayingHeader({ gameLabel, roomName, theme }: PartyHostPlayingHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="party-host-playing-header">
      <div className="header-game-name party-host-playing-header__game">
        <span className="party-host-playing-header__muted">{t('gameplay.partyHost.gameLabel')}</span>
        <span className="party-host-playing-header__title">{gameLabel}</span>
      </div>
      <div className="header-game-name party-host-playing-header__divider" aria-hidden />
      <div className="party-host-playing-header__room">
        <span className="party-host-playing-header__muted">{t('gameplay.partyHost.roomCodeLabel')}</span>
        <div
          className="party-host-playing-header__code"
          style={{
            color: theme.primary,
            backgroundColor: theme.primaryLight,
            borderColor: theme.primaryBorder,
          }}
        >
          {roomName}
        </div>
      </div>
    </div>
  );
}

type PartyHostPlayingChromeProps = {
  gameLabel: string;
  roomName: string;
  theme: PartyHostTheme;
  onBackToGameSelection?: () => void;
  onRestart: () => void;
  children: ReactNode;
};

/** `.app` + Header with party center content; children are the game layout. */
export function PartyHostPlayingChrome({
  gameLabel,
  roomName,
  theme,
  onBackToGameSelection,
  onRestart,
  children,
}: PartyHostPlayingChromeProps) {
  const { t } = useTranslation();

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.back,
          onClick: onBackToGameSelection || (() => {}),
          tooltip: t('gameplay.backToGameSelection'),
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: onRestart,
          tooltip: t('gameplay.restartGame'),
        }}
        centerContent={
          <PartyHostPlayingHeader gameLabel={gameLabel} roomName={roomName} theme={theme} />
        }
      />
      {children}
    </div>
  );
}
